// Gemeinsamer Rahmen aller Berichtsseiten (Agent 7A): Kopf mit gespeicherten Berichten, Reiter der Berichtsarten, Filterleiste, Inhalt.
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { berichteApi, type BerichtArt } from "../api/berichte";
import { fehlerText } from "../api/client";
import { FehlerBox } from "../components/Fehler";
import { useMeldung } from "../components/Meldungen";
import type { MenuePunkt } from "../components/Menue";
import { useSeitenKopf } from "../daten/seite";
import { FilterLeiste } from "./FilterLeiste";
import { herunterladen } from "./format";
import { BerichtKopf, type Steuerung } from "./steuerung";
import { BT, PFADE } from "./texte";
import { suchparameterBehalten, type useBerichtFilter } from "./zustand";
import "./berichte.css";

type Filter = ReturnType<typeof useBerichtFilter>;

const ARTEN: BerichtArt[] = ["uebersicht", "vergleich", "anrufe", "statuswechsel", "trichter", "gesendet"];

export function Reiter({ suche }: { suche: string }) {
  const behalten = suchparameterBehalten(suche);
  return (
    <nav className="bericht-reiter" aria-label="Berichtsarten">
      {ARTEN.map((a) => (
        <NavLink key={a} to={`${PFADE[a]}${behalten}`} end={a === "uebersicht"} className={({ isActive }) => `bericht-reiter__punkt${isActive ? " bericht-reiter__punkt--an" : ""}`}>
          {BT.reiter[a]}
        </NavLink>
      ))}
    </nav>
  );
}

export interface CsvEintrag {
  titel: string;
  art: "abfrage" | "anrufe" | "statuswechsel" | "trichter" | "gesendet";
  parameter: Record<string, unknown>;
}

interface Props {
  art: BerichtArt;
  filter: Filter;
  steuerung: Steuerung;
  children: ReactNode;
  ohneVergleich?: boolean;
  ohneNutzer?: boolean;
  weitereFilter?: ReactNode;
  /** CSV: Eingabe für `POST /api/berichte/export` (art, parameter) mit Menütext */
  csv?: CsvEintrag[];
  /** Hinweis oder Fehler über dem Inhalt */
  fehler?: unknown;
  onNochmal?: () => void;
  eingeschraenkt?: boolean;
}

export function BerichtSeite({ art, filter, steuerung, children, ohneVergleich, ohneNutzer, weitereFilter, csv = [], fehler, onNochmal }: Props) {
  useSeitenKopf([{ label: "Reports", to: "/reports" }, ...(art === "uebersicht" ? [] : [{ label: BT.reiter[art] }])]);
  const meldung = useMeldung();
  const laden = async (titel: string, a: CsvEintrag) => {
    try {
      const text = await berichteApi.export(a.art, a.parameter);
      herunterladen(`bericht-${a.art}-${filter.tage.von}_${filter.tage.bis}.csv`, text);
      meldung.ok(`${titel}: Datei geladen.`);
    } catch (e) {
      meldung.zeige("fehler", fehlerText(e));
    }
  };
  const punkte: MenuePunkt[] = csv.map((c) => ({ label: c.titel, icon: "extern", onClick: () => void laden(c.titel, c) }));
  return (
    <div className="liste-seite bericht-seite">
      <BerichtKopf steuerung={steuerung} zusatzPunkte={punkte} />
      <Reiter suche={filter.suche} />
      <FilterLeiste filter={filter} ohneVergleich={ohneVergleich} ohneNutzer={ohneNutzer} weitere={weitereFilter} />
      <div className="bericht-inhalt">
        {fehler ? <FehlerBox fehler={fehler} onNochmal={onNochmal} /> : null}
        {steuerung.fehler ? <FehlerBox fehler={steuerung.fehler} kompakt /> : null}
        {children}
      </div>
    </div>
  );
}
